import { useState, useMemo, useEffect } from 'react';
import {
  Button, Checkbox, Modal, Form, Input, InputNumber, Select,
  Tag, DatePicker, Radio, message, Empty, Spin, Card, Typography,
} from 'antd';
import {
  HeartOutlined, CalendarOutlined, DollarOutlined,
  CheckCircleOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, CustomerServiceOutlined, CameraOutlined, SkinOutlined,
  GiftOutlined, CarOutlined, CoffeeOutlined, StarOutlined, SoundOutlined,
  AppstoreOutlined, BankOutlined, PhoneOutlined,
  ArrowLeftOutlined, TeamOutlined, DownOutlined, WalletOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import usePublicStore from '../../store/publicStore';
import { useWeddingPlannerStore } from '../../stores/weddingPlannerStore';
import { formatMoney, paymentStatuses } from './weddingPlannerData';
import WeddingGuestsPage from './WeddingGuestsPage';
import './WeddingPlanner.css';

const { TextArea } = Input;
const { Text } = Typography;
const P = '#5F33E1';

// Icon map
const ICONS = {
  HomeOutlined: <HomeOutlined />,
  CustomerServiceOutlined: <CustomerServiceOutlined />,
  CameraOutlined: <CameraOutlined />,
  SkinOutlined: <SkinOutlined />,
  GiftOutlined: <GiftOutlined />,
  CarOutlined: <CarOutlined />,
  CoffeeOutlined: <CoffeeOutlined />,
  StarOutlined: <StarOutlined />,
  SoundOutlined: <SoundOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
};

// Auto-derive payment status from amount/paid
function derivePaymentStatus(amount, paid) {
  const a = Number(amount) || 0;
  const p = Number(paid) || 0;
  if (a > 0 && p >= a) return 'paid';
  if (p > 0) return 'partial';
  return 'unpaid';
}

function WeddingPlanner() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [expenseForm] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [createForm] = Form.useForm();

  // Store
  const {
    plans, currentPlan: plan, categories, tasks, expenses,
    loading, fetchPlans, fetchPlan, createPlan, updatePlan, deletePlan,
    addCategory, deleteCategory: storeDeleteCategory,
    addTask, updateTask, deleteTask: storeDeleteTask,
    addExpense, updateExpense,
    fetchGuests,
  } = useWeddingPlannerStore();

  // UI state
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addTaskModal, setAddTaskModal] = useState(false);
  const [addTaskCategoryId, setAddTaskCategoryId] = useState(null);
  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [activeTab, setActiveTab] = useState('planner'); // planner | guests
  const [showCreate, setShowCreate] = useState(false);
  const [openCats, setOpenCats] = useState({}); // accordion open state

  const { regions, fetchRegions, fetchDistricts } = usePublicStore();

  // Load data on mount
  useEffect(() => {
    fetchPlans();
    fetchRegions();
  }, []);

  // Birinchi rejani avtomatik yuklash
  useEffect(() => {
    if (plans.length > 0 && !plan) {
      fetchPlan(plans[0].id);
    }
  }, [plans]);

  useEffect(() => {
    if (plan?.region_id) fetchDistricts(plan.region_id);
  }, [plan?.region_id]);

  // === CALCULATIONS ===
  const stats = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.is_completed).length;
    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const totalEstimated = tasks.reduce((sum, t) => sum + (Number(t.estimated_cost) || 0), 0);
    const totalSpent = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const totalPaid = expenses.reduce((sum, e) => sum + (Number(e.paid_amount) || 0), 0);
    const budget = Number(plan?.total_budget) || totalEstimated;
    const remaining = budget - totalSpent;
    const fromSystem = expenses.filter(e => e.is_from_system).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const fromOutside = expenses.filter(e => !e.is_from_system).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const unpaidAmount = totalSpent - totalPaid;

    const spentPct = budget > 0 ? Math.min(100, Math.round((totalSpent / budget) * 100)) : 0;
    const paidPct = totalSpent > 0 ? Math.round((totalPaid / totalSpent) * 100) : 0;
    const sysPct = totalSpent > 0 ? Math.round((fromSystem / totalSpent) * 100) : 0;

    return {
      totalTasks, completedTasks, progress,
      totalEstimated, totalSpent, totalPaid, budget,
      remaining, fromSystem, fromOutside, unpaidAmount,
      spentPct, paidPct, sysPct,
    };
  }, [tasks, expenses, plan?.total_budget]);

  // Wedding countdown
  const countdown = useMemo(() => {
    if (!plan?.wedding_date) return null;
    return dayjs(plan.wedding_date).startOf('day').diff(dayjs().startOf('day'), 'day');
  }, [plan?.wedding_date]);

  // Category stats
  const categoryStats = useMemo(() => {
    return categories.map(cat => {
      const catTasks = tasks.filter(t => t.category_id === cat.id);
      const completed = catTasks.filter(t => t.is_completed).length;
      const catExpenses = expenses.filter(e => {
        const task = tasks.find(t => t.id === e.task_id);
        return task && task.category_id === cat.id;
      });
      const spent = catExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
      const estimated = catTasks.reduce((sum, t) => sum + (Number(t.estimated_cost) || 0), 0);
      const pct = catTasks.length > 0 ? Math.round((completed / catTasks.length) * 100) : 0;
      return { ...cat, tasks: catTasks, completed, total: catTasks.length, spent, estimated, pct };
    });
  }, [tasks, expenses, categories]);

  // Open first category by default once loaded
  useEffect(() => {
    if (categoryStats.length && Object.keys(openCats).length === 0) {
      setOpenCats({ [categoryStats[0].id]: true });
    }
  }, [categoryStats.length]);

  // === HANDLERS ===
  const handleTaskToggle = (taskId, checked) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    if (checked) {
      setCurrentTask(task);
      const existingExpense = expenses.find(e => e.task_id === taskId);
      if (existingExpense) {
        setEditingExpense(existingExpense);
        expenseForm.setFieldsValue({
          vendorName: existingExpense.vendor_name,
          vendorPhone: existingExpense.vendor_phone,
          isFromSystem: !!existingExpense.is_from_system,
          amount: existingExpense.amount,
          paidAmount: existingExpense.paid_amount,
          notes: existingExpense.notes,
        });
      } else {
        setEditingExpense(null);
        expenseForm.resetFields();
        expenseForm.setFieldsValue({
          amount: task.estimated_cost || 0,
          paidAmount: 0,
          isFromSystem: false,
        });
      }
      setExpenseModalOpen(true);
    } else {
      Modal.confirm({
        title: "Vazifani bekor qilasizmi?",
        content: "Bu bilan bog'liq xarajat ma'lumoti saqlanib qoladi.",
        okText: "Ha",
        cancelText: "Yo'q",
        onOk: () => updateTask(taskId, { is_completed: false }),
      });
    }
  };

  const handleExpenseSubmit = async (values) => {
    if (!currentTask) return;

    const paymentStatus = derivePaymentStatus(values.amount, values.paidAmount);

    const expenseData = {
      task_id: currentTask.id,
      vendor_name: values.vendorName,
      vendor_phone: values.vendorPhone || '',
      is_from_system: values.isFromSystem || false,
      service_type: categories.find(c => c.id === currentTask.category_id)?.name || 'Boshqa',
      amount: values.amount || 0,
      paid_amount: values.paidAmount || 0,
      payment_status: paymentStatus,
      notes: values.notes || '',
      expense_date: dayjs().format('YYYY-MM-DD'),
    };

    if (editingExpense) {
      await updateExpense(editingExpense.id, expenseData);
    } else {
      await addExpense(expenseData);
    }

    // Mark task as completed
    await updateTask(currentTask.id, { is_completed: true });

    setExpenseModalOpen(false);
    setCurrentTask(null);
    setEditingExpense(null);
    expenseForm.resetFields();
    message.success("Xarajat ma'lumoti saqlandi!");
  };

  const openEditExpense = (task, taskExpense) => {
    setCurrentTask(task);
    setEditingExpense(taskExpense);
    expenseForm.setFieldsValue({
      vendorName: taskExpense.vendor_name,
      vendorPhone: taskExpense.vendor_phone,
      isFromSystem: !!taskExpense.is_from_system,
      amount: taskExpense.amount,
      paidAmount: taskExpense.paid_amount,
      notes: taskExpense.notes,
    });
    setExpenseModalOpen(true);
  };

  const handleDeleteTask = (taskId) => storeDeleteTask(taskId);

  const handleAddTask = async (values) => {
    await addTask({
      category_id: addTaskCategoryId,
      title: values.title,
      estimated_cost: values.estimatedCost || 0,
    });
    setAddTaskModal(false);
    form.resetFields();
    message.success("Vazifa qo'shildi!");
  };

  const handleAddCategory = async (values) => {
    await addCategory({
      name: values.categoryName,
      icon: 'AppstoreOutlined',
      color: values.categoryColor || '#607D8B',
    });
    setAddCategoryModal(false);
    categoryForm.resetFields();
    message.success("Kategoriya qo'shildi!");
  };

  const handleDeleteCategory = (catId) => {
    const catTasks = tasks.filter(t => t.category_id === catId);
    if (catTasks.length > 0) {
      Modal.confirm({
        title: "Kategoriyani o'chirasizmi?",
        content: `Bu kategoriyada ${catTasks.length} ta vazifa bor. Barchasi o'chib ketadi.`,
        okText: "Ha",
        okButtonProps: { danger: true },
        cancelText: "Yo'q",
        onOk: () => storeDeleteCategory(catId),
      });
    } else {
      storeDeleteCategory(catId);
    }
  };

  const handleResetAll = () => {
    if (!plan) return;
    Modal.confirm({
      title: "Rejani o'chirasizmi?",
      content: "Reja, vazifalar, xarajatlar va mehmonlar o'chib ketadi!",
      okText: "Ha, o'chir",
      okButtonProps: { danger: true },
      cancelText: "Bekor qilish",
      onOk: async () => {
        await deletePlan(plan.id);
        setSettingsOpen(false);
        message.success("Reja o'chirildi");
      },
    });
  };

  const handleCreatePlan = async (values) => {
    const result = await createPlan({
      title: values.title,
      wedding_date: values.wedding_date?.format('YYYY-MM-DD') || null,
      total_budget: values.total_budget || 0,
      region_id: values.region_id || null,
      district_id: values.district_id || null,
    });
    if (result) {
      setShowCreate(false);
      createForm.resetFields();
      message.success("Reja yaratildi!");
    }
  };

  const toggleCat = (id) => setOpenCats(prev => ({ ...prev, [id]: !prev[id] }));

  // ═══════════════════ LOADING ═══════════════════
  if (loading && !plan) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  // ═══════════════════ CREATE PLAN VIEW ═══════════════════
  if (!plan || showCreate) {
    return (
      <div className="wp-page">
        <div className="wp-create-hero">
          <div className="ico"><HeartOutlined /></div>
          <h1>To'y Rejalashtiruvchi</h1>
          <p>Keling, maxsus kuningizni birga rejalashtiramiz</p>
        </div>

        <div className="wp-create">
          <Card style={{ borderRadius: 18, borderColor: 'var(--qk-border)', background: 'var(--qk-card-bg)' }}>
            <Form form={createForm} layout="vertical" onFinish={handleCreatePlan} size="large">
              <Form.Item name="title" label="To'y nomi" rules={[{ required: true, message: "Nomini kiriting" }]}>
                <Input placeholder="Masalan: Aziz & Malika to'yi" style={{ borderRadius: 10 }} />
              </Form.Item>
              <Form.Item name="wedding_date" label="To'y sanasi">
                <DatePicker
                  style={{ width: '100%', borderRadius: 10 }}
                  placeholder="Sanani tanlang"
                  disabledDate={current => current && current < dayjs().startOf('day')}
                  format="DD.MM.YYYY"
                />
              </Form.Item>
              <Form.Item name="total_budget" label="Umumiy byudjet (so'm)">
                <InputNumber
                  min={0} style={{ width: '100%', borderRadius: 10 }}
                  placeholder="50 000 000"
                  formatter={v => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : ''}
                  parser={v => v?.replace(/\s/g, '')}
                />
              </Form.Item>
              <Form.Item name="region_id" label="Viloyat">
                <Select
                  options={regions.map(r => ({ value: r.id, label: r.nameUz }))}
                  allowClear placeholder="Tanlang" style={{ width: '100%' }}
                />
              </Form.Item>
              <Button className="wp-btn-primary" htmlType="submit" block loading={loading}
                style={{ height: 48, fontSize: 16 }}>
                Boshlash
              </Button>
              {plan && (
                <Button type="text" block style={{ marginTop: 8 }} onClick={() => setShowCreate(false)}>
                  Bekor qilish
                </Button>
              )}
            </Form>
          </Card>
        </div>
      </div>
    );
  }

  // ═══════════════════ MAIN PAGE ═══════════════════
  return (
    <>
      <div className="wp-page">
        {/* HERO */}
        <div className="wp-hero">
          <div className="wp-hero-inner">
            <div className="wp-hero-title">
              <div className="wp-hero-heart"><HeartOutlined /></div>
              <div>
                <h1>{plan.title}</h1>
                {plan.wedding_date && (
                  <div className="wp-hero-date">
                    <CalendarOutlined /> {dayjs(plan.wedding_date).format('DD MMMM YYYY')}
                  </div>
                )}
              </div>
            </div>
            <div className="wp-hero-actions">
              {countdown !== null && (
                <div className="wp-countdown">
                  {countdown > 0 ? (
                    <><b>{countdown}</b><span>kun qoldi</span></>
                  ) : countdown === 0 ? (
                    <><b>🎉</b><span>Bugun!</span></>
                  ) : (
                    <><b>✓</b><span>O'tdi</span></>
                  )}
                </div>
              )}
              <button className="wp-hero-btn" onClick={() => setSettingsOpen(!settingsOpen)} title="Sozlamalar">
                {settingsOpen ? <ArrowLeftOutlined /> : <EditOutlined />}
              </button>
            </div>
          </div>
        </div>

        <div className="wp-shell">
          {/* Tabs */}
          <div className="wp-tabs">
            <button className={`wp-tab ${activeTab === 'planner' ? 'active' : ''}`}
              onClick={() => setActiveTab('planner')}>
              <CheckCircleOutlined /> Vazifalar
            </button>
            <button className={`wp-tab ${activeTab === 'guests' ? 'active' : ''}`}
              onClick={() => { setActiveTab('guests'); fetchGuests(); }}>
              <TeamOutlined /> Mehmonlar
            </button>
          </div>

          {/* Settings panel */}
          {settingsOpen && (
            <div className="wp-card">
              <div className="wp-card-title"><EditOutlined /> Reja sozlamalari</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Reja nomi</Text>
                  <Input value={plan.title} onChange={e => updatePlan({ title: e.target.value })} style={{ borderRadius: 10 }} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>To'y sanasi</Text>
                  <DatePicker
                    value={plan.wedding_date ? dayjs(plan.wedding_date) : null}
                    onChange={d => updatePlan({ wedding_date: d ? d.format('YYYY-MM-DD') : null })}
                    style={{ width: '100%', borderRadius: 10 }} format="DD.MM.YYYY"
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Umumiy byudjet</Text>
                  <InputNumber
                    value={plan.total_budget}
                    onChange={v => updatePlan({ total_budget: v || 0 })}
                    style={{ width: '100%', borderRadius: 10 }}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    parser={v => v.replace(/\s/g, '')}
                    min={0} addonAfter="so'm"
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Viloyat</Text>
                  <Select
                    value={plan.region_id}
                    onChange={v => updatePlan({ region_id: v, district_id: null })}
                    options={regions.map(r => ({ value: r.id, label: r.nameUz }))}
                    allowClear placeholder="Tanlang" style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
                <Button size="small" icon={<PlusOutlined />} onClick={() => setShowCreate(true)} style={{ borderRadius: 10 }}>
                  Yangi reja
                </Button>
                <Button danger size="small" onClick={handleResetAll} style={{ borderRadius: 10 }}>
                  Rejani o'chirish
                </Button>
              </div>
            </div>
          )}

          {/* PLANNER TAB */}
          {activeTab === 'planner' && (
            <>
              {/* Budget dashboard */}
              <div className="wp-card">
                <div className="wp-card-title"><WalletOutlined /> Byudjet</div>

                <div className="wp-budget-total">
                  <span className="big">{formatMoney(stats.budget)}</span>
                  <span className="lbl">umumiy byudjet</span>
                </div>

                {/* Spent vs budget */}
                <div className="wp-bar spent"><i style={{ width: `${stats.spentPct}%` }} /></div>
                <div className="wp-bar-row">
                  <span>Sarflangan: <b>{formatMoney(stats.totalSpent)}</b></span>
                  <span>{stats.spentPct}%</span>
                </div>

                {/* Paid vs spent */}
                {stats.totalSpent > 0 && (
                  <>
                    <div className="wp-bar paid" style={{ marginTop: 12 }}><i style={{ width: `${stats.paidPct}%` }} /></div>
                    <div className="wp-bar-row">
                      <span>To'langan: <b>{formatMoney(stats.totalPaid)}</b></span>
                      <span>{stats.paidPct}%</span>
                    </div>
                  </>
                )}

                {/* Mini stats */}
                <div className="wp-stat-grid">
                  <div className="wp-stat">
                    <div className="v" style={{ color: P }}>{formatMoney(stats.totalSpent)}</div>
                    <div className="k">Sarflangan</div>
                  </div>
                  <div className="wp-stat">
                    <div className="v" style={{ color: 'var(--qk-success)' }}>{formatMoney(stats.totalPaid)}</div>
                    <div className="k">To'langan</div>
                  </div>
                  <div className="wp-stat">
                    <div className="v" style={{ color: stats.remaining < 0 ? '#ff4d4f' : '#1890ff' }}>
                      {formatMoney(stats.remaining)}
                    </div>
                    <div className="k">{stats.remaining < 0 ? 'Oshib ketdi' : 'Qoldiq'}</div>
                  </div>
                  <div className="wp-stat">
                    <div className="v" style={{ color: stats.unpaidAmount > 0 ? '#faad14' : 'var(--qk-text-muted)' }}>
                      {formatMoney(stats.unpaidAmount)}
                    </div>
                    <div className="k">Qarz</div>
                  </div>
                </div>

                {/* Source split */}
                {stats.totalSpent > 0 && (
                  <>
                    <div className="wp-split">
                      <i style={{ width: `${stats.sysPct}%`, background: P }} />
                      <i style={{ width: `${100 - stats.sysPct}%`, background: '#FF9800' }} />
                    </div>
                    <div className="wp-split-legend">
                      <div className="wp-legend-item">
                        <span className="wp-dot" style={{ background: P }} />
                        Tizimdan: <b>{formatMoney(stats.fromSystem)}</b>
                      </div>
                      <div className="wp-legend-item">
                        <span className="wp-dot" style={{ background: '#FF9800' }} />
                        Tashqaridan: <b>{formatMoney(stats.fromOutside)}</b>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Tasks progress */}
              <div className="wp-card wp-progress-card">
                <div className="wp-ring" style={{ '--pct': stats.progress }}>
                  <span>{stats.progress}%</span>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--qk-text)' }}>
                    {stats.completedTasks} / {stats.totalTasks} vazifa bajarildi
                  </div>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {stats.progress === 100 ? "Hammasi tayyor! 🎉" : "Rejangiz bo'yicha davom eting"}
                  </Text>
                </div>
              </div>

              {/* Categories */}
              <div style={{ marginTop: 16 }}>
                {categoryStats.length === 0 ? (
                  <div className="wp-card"><Empty description="Vazifalar yo'q" image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>
                ) : (
                  categoryStats.map(cat => {
                    const isOpen = !!openCats[cat.id];
                    return (
                      <div key={cat.id} className={`wp-cat ${isOpen ? 'open' : ''}`}>
                        <div className="wp-cat-head" style={{ borderLeftColor: cat.color }} onClick={() => toggleCat(cat.id)}>
                          <span className="wp-cat-icon" style={{ background: cat.color + '22', color: cat.color }}>
                            {ICONS[cat.icon] || <AppstoreOutlined />}
                          </span>
                          <div className="wp-cat-meta">
                            <div className="wp-cat-name">{cat.name}</div>
                            <div className="wp-cat-sub">{cat.completed}/{cat.total} vazifa{cat.spent > 0 ? ` • ${formatMoney(cat.spent)}` : ''}</div>
                            <div className="wp-cat-mini-bar">
                              <i style={{ width: `${cat.pct}%`, background: cat.color }} />
                            </div>
                          </div>
                          <div className="wp-cat-right">
                            {cat.spent > 0 && (
                              <span className="wp-cat-spent" style={{ color: cat.spent > cat.estimated && cat.estimated > 0 ? '#ff4d4f' : 'var(--qk-success)' }}>
                                {formatMoney(cat.spent)}
                              </span>
                            )}
                            <DownOutlined className="wp-chevron" />
                          </div>
                        </div>

                        {isOpen && (
                          <div className="wp-cat-body">
                            {cat.tasks.map(task => {
                              const taskExpense = expenses.find(e => e.task_id === task.id);
                              const ps = taskExpense && paymentStatuses.find(p => p.value === taskExpense.payment_status);
                              return (
                                <div key={task.id} className={`wp-task ${task.is_completed ? 'done' : ''}`}>
                                  <div className="wp-task-main">
                                    <Checkbox checked={task.is_completed} onChange={e => handleTaskToggle(task.id, e.target.checked)} />
                                    <div className="wp-task-body">
                                      <div className="wp-task-title">{task.title}</div>
                                      {taskExpense && (
                                        <div className="wp-task-tags">
                                          <Tag color={taskExpense.is_from_system ? 'purple' : 'orange'} style={{ borderRadius: 6, fontSize: 10, margin: 0 }}>
                                            {taskExpense.is_from_system ? 'Tizimdan' : 'Tashqaridan'}
                                          </Tag>
                                          <span className="wp-task-vendor">{taskExpense.vendor_name} — {formatMoney(taskExpense.amount)}</span>
                                          {ps && <Tag color={ps.color} style={{ borderRadius: 6, fontSize: 10, margin: 0 }}>{ps.label}</Tag>}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="wp-task-actions">
                                    {Number(task.estimated_cost) > 0 && !taskExpense && (
                                      <span className="wp-task-est">~{formatMoney(task.estimated_cost)}</span>
                                    )}
                                    {task.is_completed && taskExpense && (
                                      <Button type="text" size="small" icon={<EditOutlined />}
                                        onClick={() => openEditExpense(task, taskExpense)} style={{ color: P }} />
                                    )}
                                    <Button type="text" size="small" icon={<DeleteOutlined />}
                                      onClick={() => handleDeleteTask(task.id)} style={{ color: '#ff4d4f' }} />
                                  </div>
                                </div>
                              );
                            })}
                            <Button type="dashed" size="small" icon={<PlusOutlined />} block className="wp-add-task"
                              onClick={() => { setAddTaskCategoryId(cat.id); form.resetFields(); setAddTaskModal(true); }}
                              style={{ borderRadius: 10 }}>
                              Vazifa qo'shish
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                <Button type="dashed" block icon={<PlusOutlined />} className="wp-add-cat"
                  onClick={() => { categoryForm.resetFields(); setAddCategoryModal(true); }}
                  style={{ borderRadius: 12, height: 44, fontWeight: 600, borderColor: P, color: P }}>
                  Kategoriya qo'shish
                </Button>
              </div>
            </>
          )}

          {activeTab === 'guests' && <WeddingGuestsPage />}
        </div>
      </div>

      {/* Expense Modal */}
      <Modal
        open={expenseModalOpen}
        onCancel={() => {
          setExpenseModalOpen(false);
          setCurrentTask(null);
          setEditingExpense(null);
          expenseForm.resetFields();
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarOutlined style={{ color: P, fontSize: 18 }} />
            <span>Xarajat ma'lumotlari</span>
          </div>
        }
        footer={null} width={480} destroyOnClose
      >
        {currentTask && (
          <div className="wp-pay-preview">
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircleOutlined style={{ color: 'var(--qk-success)' }} /> {currentTask.title}
            </span>
            {Number(currentTask.estimated_cost) > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>~{formatMoney(currentTask.estimated_cost)}</Text>
            )}
          </div>
        )}

        <Form form={expenseForm} layout="vertical" onFinish={handleExpenseSubmit} requiredMark={false}>
          <Form.Item name="vendorName" label="Xizmat ko'rsatuvchi nomi"
            rules={[{ required: true, message: 'Nomini kiriting' }]}>
            <Input prefix={<BankOutlined style={{ color: '#bbb' }} />}
              placeholder="Masalan: Fayz to'yxonasi" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="vendorPhone" label="Telefon raqami">
            <Input prefix={<PhoneOutlined style={{ color: '#bbb' }} />}
              placeholder="+998 90 123 45 67" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="isFromSystem" label="Buyurtma qayerdan berilgan?">
            <Radio.Group optionType="button" buttonStyle="solid">
              <Radio.Button value={true}>Tizimdan (Quvonchli kun)</Radio.Button>
              <Radio.Button value={false}>Tashqaridan</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="amount" label="Kelishilgan summa"
              rules={[{ required: true, message: 'Summani kiriting' }]} style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%', borderRadius: 10 }}
                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={v => v.replace(/\s/g, '')} min={0} addonAfter="so'm" />
            </Form.Item>
            <Form.Item name="paidAmount" label="To'langan" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%', borderRadius: 10 }}
                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={v => v.replace(/\s/g, '')} min={0} addonAfter="so'm" />
            </Form.Item>
          </div>
          {/* Auto-derived payment status preview */}
          <Form.Item shouldUpdate noStyle>
            {() => {
              const a = expenseForm.getFieldValue('amount');
              const p = expenseForm.getFieldValue('paidAmount');
              const st = paymentStatuses.find(s => s.value === derivePaymentStatus(a, p));
              return (
                <div className="wp-pay-preview">
                  <span>To'lov holati</span>
                  <Tag color={st?.color} style={{ borderRadius: 8, margin: 0 }}>{st?.label}</Tag>
                </div>
              );
            }}
          </Form.Item>
          <Form.Item name="notes" label="Izoh">
            <TextArea rows={2} placeholder="Qo'shimcha ma'lumot..." style={{ borderRadius: 10 }} maxLength={500} />
          </Form.Item>
          <Button className="wp-btn-primary" htmlType="submit" block size="large" icon={<CheckCircleOutlined />}
            style={{ height: 46 }}>
            Saqlash
          </Button>
        </Form>
      </Modal>

      {/* Add Task Modal */}
      <Modal open={addTaskModal} onCancel={() => { setAddTaskModal(false); form.resetFields(); }}
        title="Yangi vazifa" footer={null} width={400} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleAddTask} requiredMark={false}>
          <Form.Item name="title" label="Vazifa nomi" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input placeholder="Masalan: DJ band qilish" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="estimatedCost" label="Taxminiy narx">
            <InputNumber style={{ width: '100%', borderRadius: 10 }}
              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={v => v.replace(/\s/g, '')} min={0} addonAfter="so'm" />
          </Form.Item>
          <Button className="wp-btn-primary" htmlType="submit" block>Qo'shish</Button>
        </Form>
      </Modal>

      {/* Add Category Modal */}
      <Modal open={addCategoryModal} onCancel={() => { setAddCategoryModal(false); categoryForm.resetFields(); }}
        title="Yangi kategoriya" footer={null} width={400} destroyOnClose>
        <Form form={categoryForm} layout="vertical" onFinish={handleAddCategory} requiredMark={false}>
          <Form.Item name="categoryName" label="Kategoriya nomi" rules={[{ required: true, message: 'Nomini kiriting' }]}>
            <Input placeholder="Masalan: Sovg'alar, Sartarosh..." style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="categoryColor" label="Rang">
            <Select placeholder="Rangni tanlang" options={[
              { value: '#5F33E1', label: 'Binafsha' }, { value: '#E91E63', label: 'Pushti' },
              { value: '#FF9800', label: 'Sariq' }, { value: '#4CAF50', label: 'Yashil' },
              { value: '#00BCD4', label: 'Zangori' }, { value: '#795548', label: 'Jigarrang' },
              { value: '#FF5722', label: 'Qizil' }, { value: '#3F51B5', label: "Ko'k" },
              { value: '#9C27B0', label: 'Siyoh' }, { value: '#607D8B', label: 'Kulrang' },
            ]} />
          </Form.Item>
          <Button className="wp-btn-primary" htmlType="submit" block>Qo'shish</Button>
        </Form>
      </Modal>
    </>
  );
}

export default WeddingPlanner;
